import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import { Modal, Spinner } from "./ui.jsx";

/**
 * Choosing a thing that lives somewhere else.
 *
 * A domain comes from Cloudflare and a repository from GitLab, and neither fits
 * behind a text field: there are eighty-three repositories and twenty-four zones,
 * and the useful information about each — is it already onboarded, does this name
 * point at this box — is a second line that a `<datalist>` cannot show.
 *
 * Two levels, because that is the shape of the data. Zones then the names inside
 * one; repositories are flat and simply skip the first level.
 */
export function Picker({ open, onClose, title, onPick, load, loadChildren, childTitle, hint }) {
    const [items, setItems] = useState(null);
    const [parent, setParent] = useState(null);
    const [children, setChildren] = useState(null);
    const [query, setQuery] = useState("");
    const [error, setError] = useState(null);
    const search = useRef(null);

    // Held in a ref, not in the dependency list: the callers build `load` inline,
    // so it is a new function on every render of the form around it — and the form
    // re-renders while you are typing in here. Depending on it would empty the list
    // under your cursor.
    const loaders = useRef({ load, loadChildren });
    loaders.current = { load, loadChildren };

    useEffect(() => {
        if (!open) return;
        setParent(null);
        setChildren(null);
        setQuery("");
        setError(null);
        setItems(null);
        loaders.current
            .load()
            .then(setItems)
            .catch((problem) => {
                setError(problem.message);
                setItems([]);
            });
    }, [open]);

    // The search is the point of opening this, so it gets the cursor.
    useEffect(() => {
        if (open) setTimeout(() => search.current?.focus(), 50);
    }, [open, parent]);

    const openParent = (item) => {
        setParent(item);
        setChildren(null);
        setQuery("");
        loaders.current
            .loadChildren(item)
            .then(setChildren)
            .catch((problem) => {
                setError(problem.message);
                setChildren([]);
            });
    };

    const list = parent ? children : items;
    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle || !list) return list ?? [];
        return list.filter((item) => `${item.label} ${item.detail ?? ""}`.toLowerCase().includes(needle));
    }, [list, query]);

    return (
        <Modal open={open} onClose={onClose} title={parent ? childTitle?.(parent) ?? parent.label : title} width="max-w-2xl">
            <div className="space-y-3">
                {parent ? (
                    <button className="btn-ghost px-2 py-1 text-xs" onClick={() => { setParent(null); setChildren(null); setQuery(""); }}>
                        <ChevronLeft size={13} /> All {title.toLowerCase()}
                    </button>
                ) : null}

                <div className="relative">
                    <Search size={15} className="muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden />
                    <input
                        ref={search}
                        className="input pl-8"
                        placeholder={parent ? `Search in ${parent.label}` : `Search ${title.toLowerCase()}`}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        aria-label={`Search ${title.toLowerCase()}`}
                    />
                </div>

                {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
                {hint && !parent ? <p className="muted text-xs">{hint}</p> : null}

                {!list ? (
                    <div className="py-8 text-center"><Spinner className="muted" /></div>
                ) : shown.length === 0 ? (
                    <p className="muted py-8 text-center text-sm">Nothing matches.</p>
                ) : (
                    <ul className="max-h-[26rem] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
                        {shown.map((item) => (
                            <li key={item.id ?? item.label}>
                                <button
                                    onClick={() => (item.children ? openParent(item) : (onPick(item), onClose()))}
                                    disabled={item.disabled}
                                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-800/60"
                                >
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-medium">{item.label}</span>
                                        {item.detail ? <span className="muted block truncate text-xs">{item.detail}</span> : null}
                                    </span>
                                    {item.badge ? <span className={`shrink-0 ${item.badgeClass ?? "badge-queued"}`}>{item.badge}</span> : null}
                                    {item.children ? <ChevronRight size={15} className="muted shrink-0" aria-hidden /> : null}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Modal>
    );
}
